/**
 * Listen and Choose Exercise (INT-CMP-006)
 * User listens to audio and selects the correct answer from options.
 * Falls back to showing the text if audio is unavailable.
 */

import React, { useState, useCallback } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useKeyboardShortcut, useNumberKeyShortcuts } from '../../hooks/useKeyboardShortcut';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface ListenAndChooseExerciseProps {
  prompt: string;
  audioText: string;
  options: string[];
  correctAnswer: string;
  hint?: string;
  onComplete: (correct: boolean, score: number) => void;
}

export const ListenAndChooseExercise = React.memo(function ListenAndChooseExercise({
  prompt,
  audioText,
  options,
  correctAnswer,
  hint,
  onComplete,
}: ListenAndChooseExerciseProps) {
  const { theme: t, fontScale, ffs } = useDisplayTheme();

  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const isCorrect = selectedAnswer === correctAnswer;

  const handleOptionPress = useCallback((option: string) => {
    if (submitted) return;
    setSelectedAnswer(option);
  }, [submitted]);

  const handleSubmit = useCallback(() => {
    if (!selectedAnswer) return;
    setSubmitted(true);
    const correct = selectedAnswer === correctAnswer;
    onComplete(correct, correct ? 100 : 0);
  }, [selectedAnswer, correctAnswer, onComplete]);

  const handleOptionByIndex = useCallback((i: number) => {
    if (i < options.length) {
      handleOptionPress(options[i]);
    }
  }, [options, handleOptionPress]);

  // Keyboard shortcuts: number keys select options, Enter submits (web only, no-op on native)
  useNumberKeyShortcuts(options.length, handleOptionByIndex, !submitted);
  useKeyboardShortcut('Enter', handleSubmit, { enabled: !submitted && !!selectedAnswer });

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: t.card, borderWidth: 1, borderColor: t.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: t.text, ...ffs }}
        >
          {prompt}
        </Text>

        {/* Audio play area (visual representation) */}
        <Box
          p="$4"
          rounded="$lg"
          alignItems="center"
          style={{ backgroundColor: t.primarySoft }}
        >
          <HStack space="md" alignItems="center">
            <Text allowFontScaling={false} style={{ fontSize: 32 }}>🔊</Text>
            <VStack>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: t.primary, ...ffs }}
              >
                Listen to the audio
              </Text>
              <TouchableOpacity
                testID="listen-show-transcript"
                accessibilityLabel="Show transcript"
                accessibilityRole="button"
                onPress={() => setShowTranscript(!showTranscript)}
                activeOpacity={0.7}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: t.primary, ...ffs }}
                >
                  {showTranscript ? 'Hide transcript' : 'Show transcript'}
                </Text>
              </TouchableOpacity>
            </VStack>
          </HStack>
          {showTranscript && (
            <Text
              allowFontScaling={false}
              mt="$2"
              style={{ fontSize: FS_SM * fontScale, color: t.text, fontStyle: 'italic', ...ffs }}
            >
              "{audioText}"
            </Text>
          )}
        </Box>

        {hint && !submitted && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Options */}
        <VStack space="xs">
          {options.map((option, idx) => {
            const isSelected = selectedAnswer === option;
            const isOptionCorrect = option === correctAnswer;

            let bgColor: string = t.isDark ? '#374151' : '#F3F4F6';
            let borderColor: string = t.border;

            if (!submitted && isSelected) {
              bgColor = colors.primary;
              borderColor = colors.primary;
            } else if (submitted && isSelected) {
              bgColor = isCorrect ? '#D1FAE5' : '#FEE2E2';
              borderColor = isCorrect ? '#059669' : '#DC2626';
            } else if (submitted && isOptionCorrect) {
              bgColor = '#D1FAE5';
              borderColor = '#059669';
            }

            return (
              <TouchableOpacity
                key={idx}
                testID={`listen-option-${idx}`}
                accessibilityLabel={option}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                onPress={() => handleOptionPress(option)}
                disabled={submitted}
                activeOpacity={0.7}
              >
                <Box
                  px="$4"
                  py="$3"
                  rounded="$md"
                  style={{ backgroundColor: bgColor, borderWidth: 1, borderColor }}
                >
                  <Text
                    allowFontScaling={false}
                    style={{
                      fontSize: FS_SM * fontScale,
                      fontWeight: '500',
                      color: !submitted && isSelected ? '#FFFFFF' : t.text,
                      ...ffs,
                    }}
                  >
                    {option}
                  </Text>
                </Box>
              </TouchableOpacity>
            );
          })}
        </VStack>

        {/* Submit button */}
        {!submitted && selectedAnswer && (
          <TouchableOpacity
            testID="listen-submit"
            accessibilityLabel="Submit answer"
            accessibilityRole="button"
            onPress={handleSubmit}
            activeOpacity={0.7}
          >
            <Box
              py="$3"
              rounded="$md"
              alignItems="center"
              style={{ backgroundColor: colors.primary }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
              >
                Check Answer
              </Text>
            </Box>
          </TouchableOpacity>
        )}

        {/* Result */}
        {submitted && (
          <Box
            p="$2"
            rounded="$md"
            style={{ backgroundColor: isCorrect ? '#D1FAE5' : '#FEE2E2' }}
          >
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_SM * fontScale,
                fontWeight: '600',
                color: isCorrect ? '#065F46' : '#991B1B',
                ...ffs,
              }}
            >
              {isCorrect ? '✓ Correct!' : `✗ The answer is: ${correctAnswer}`}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
});
